// The venue and organisation areas (spec/13 §2): resolve the slug in the URL to one of the member's
// own venues or organisations, then draw the frame around the page — its sidebar listing the sections the
// member may open there (`sections.ts`), with an owner's "Setup · n left" while a venue sets up (spec/13 §13.1) —
// and the console tour around it, played once for a newcomer who runs a venue and again from the staff menu.

import { useTranslation } from '@clavian/i18n/react';
import { Skeleton } from '@clavian/ui-web';
import { Outlet, useNavigate, useParams } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { useMyStaffPins } from '../account/queries';
import type { MyOrganization, MyVenue } from '../api/me';
import { useAccess, useMyOrganizations, useMyVenues } from '../api/queries';
import { RealtimeProvider } from '../realtime';
import { StaffRunAlerts } from '../floor/staff-alerts';
import { AppShell, OrganizationLink, useSignOut, type NavGroup, type PaletteAction } from './AppShell';
import { LoadFailure } from './LoadFailure';
import { Icon, NavIcon } from './nav-icons';
import { NotFound } from './NotFound';
import type { SettingsTarget } from '../settings/search';
import { ScopeProvider, type ConsoleScope } from './scope';
import { ORG_GROUPS, VENUE_GROUPS, visibleGroups, type GroupSpec, type SectionSpec } from './sections';
import { ShiftStatus } from './ShiftStatus';
import { useVenueSwitcher, VenueSwitcher } from './VenueSwitcher';
import { ShiftPromptProvider } from '../org/shifts/shift-prompt';
import { useBootServers } from '../boot/queries';
import { useStockStatus } from '../org/stock/queries';
import { useSetupNavEntry, type SetupNavEntry } from '../setup/nav';
import { useConsoleTour } from '../setup/ShellTour';

/** Who sees the tour on a first visit: those who run a venue. Anyone may play it from the staff menu. */
const TOUR_ROLES = ['owner', 'manager'];

export function VenueLayout(): ReactNode {
  const { t } = useTranslation('console');
  const { venueSlug } = useParams({ from: '/signed-in/v/$venueSlug' });
  const venues = useMyVenues();
  const organizations = useMyOrganizations();
  const found = venues.data?.find((candidate) => candidate.slug === venueSlug);
  // Built before the guards below so the hook order never changes between renders.
  const switcher = useVenueSwitcher(found === undefined ? undefined : { kind: 'venue', id: found.id });
  const actions = useAreaActions(switcher.action);
  const pinMissing = useStaffPinMissing(found);
  const organization = organizations.data?.find((candidate) => candidate.id === found?.organizationId);
  const tour = useConsoleTour(organization !== undefined && TOUR_ROLES.some((role) => isRole(organization.role, role)));
  const staffItems = useStaffMenuItems({ to: '/v/$venueSlug/account', params: { venueSlug } }, tour.start, pinMissing);
  const setupEntry = useSetupNavEntry(organization, venues.data);
  // What the member may open here decides what the sidebar lists (spec/13 §1, §2).
  const access = useAccess(found === undefined ? undefined : { venueId: found.id });
  // Stock is listed where the venue tracks it — or, while it is off, to a member who can turn it on (spec/13 §6.4).
  const readsStock = found !== undefined && access.status === 'ready' && access.can('inventory:read');
  const stockStatus = useStockStatus(found?.organizationId ?? '', found?.id ?? '', readsStock);
  const stockListed = readsStock && (stockStatus.data?.enabled === true || access.can('settings:fnb'));
  // Clavian Boot is listed where the organisation has the add-on: the api answers its reads 402 without it (spec/25 §16).
  const readsBoot = found !== undefined && access.status === 'ready' && access.can('boot:view');
  const bootServers = useBootServers({ organizationId: found?.organizationId ?? '', venueId: found?.id ?? '' }, readsBoot);
  const bootListed = readsBoot && bootServers.isSuccess;

  // What was read stays on screen if a refetch fails; only a first read that failed is an error.
  if (venues.data === undefined) {
    return venues.isError ? <LoadFailure error={venues.error} onRetry={() => void venues.refetch()} /> : <Loading />;
  }
  const venue = found;
  // Not one of the member's venues: the same answer as a venue that does not exist (spec/07 §6.1).
  if (venue === undefined) return <NotFound />;

  const scope: ConsoleScope = {
    kind: 'venue',
    venue,
    organization: organizations.data?.find((organization) => organization.id === venue.organizationId),
  };
  const sectionGroups = buildGroups(visibleGroups(VENUE_GROUPS, access, { stock: stockListed, boot: bootListed }), t, (section): SectionRoute => {
    // Floor is the venue's landing screen (spec/13 §3, F-STN-14).
    if (section.id === 'floor') return { to: '/v/$venueSlug', params: { venueSlug } };
    if (section.id === 'stations') return { to: '/v/$venueSlug/stations', params: { venueSlug } };
    // Clavian Boot (spec/25 §16): `/boot` opens the Servers tab; linking there keeps the item current on every tab.
    if (section.id === 'boot') return { to: '/v/$venueSlug/boot', params: { venueSlug } };
    if (section.id === 'sell') return { to: '/v/$venueSlug/sell', params: { venueSlug } };
    // Players (F-ORG-05) and Staff and roles (F-ORG-13): organisation-scoped, reached from the venue too.
    if (section.id === 'players') return { to: '/v/$venueSlug/players', params: { venueSlug } };
    if (section.id === 'staff') return { to: '/v/$venueSlug/staff', params: { venueSlug } };
    if (section.id === 'orders') return { to: '/v/$venueSlug/orders', params: { venueSlug } };
    // `/pricing` opens the first tab; linking there keeps the item current on every tab.
    if (section.id === 'pricing') return { to: '/v/$venueSlug/pricing', params: { venueSlug } };
    // Library (F-CAT-02, spec/13 §9.1): the titles this venue offers from the platform catalogue.
    if (section.id === 'library') return { to: '/v/$venueSlug/library', params: { venueSlug } };
    // Reports (spec/13 §2, spec/17): the Games played report (F-CAT-04).
    if (section.id === 'reports') return { to: '/v/$venueSlug/reports', params: { venueSlug } };
    // Shifts, cash and the day close (spec/13 §10, F-OPS-01 … F-OPS-07).
    if (section.id === 'shifts') return { to: '/v/$venueSlug/shifts', params: { venueSlug } };
    // The Kitchen section opens the live kitchen display (spec/13 §6.2, F-FNB-04); the F&B menu setup
    // is one link away from it.
    if (section.id === 'kitchen') return { to: '/v/$venueSlug/kitchen', params: { venueSlug } };
    // Stock (spec/13 §6.4): `/stock` opens the list; linking there keeps the item current on every tab.
    if (section.id === 'stock') return { to: '/v/$venueSlug/stock', params: { venueSlug } };
    // `/settings` opens the first group; linking there keeps the item current on every group.
    if (section.id === 'settings') return { to: '/v/$venueSlug/settings', params: { venueSlug } };
    return { to: '/v/$venueSlug/soon/$section', params: { venueSlug, section: section.id } };
  });
  // This venue's own Setup while it sets up; the organisation's while another of its venues does.
  const groups = withSetupEntry(
    sectionGroups,
    setupEntry,
    venue.status === 'setup' ? { to: '/v/$venueSlug/setup', params: { venueSlug } } : organization === undefined ? undefined : { to: '/o/$orgSlug/setup', params: { orgSlug: organization.slug } },
    t,
  );
  return (
    <ScopeProvider scope={scope}>
      {/* One realtime socket for the organisation, shared by Floor, Kitchen and Orders (spec/09). */}
      <RealtimeProvider organizationId={venue.organizationId}>
        {/* ADR-0033: a staff-run station's time alerts (N-30) reach staff on every screen of the venue, with a chime. */}
        <StaffRunAlerts venueId={venue.id} />
        {/* "Start your shift" (ADR-0031): opens once after sign-in; Sell's locked Cash tender reopens it. Around the
            shell, so its sign-out asks first while the member holds a drawer (ADR-0032). */}
        <ShiftPromptProvider venue={venue}>
        <AppShell
          area={<VenueSwitcher scope={scope} open={switcher.open} onOpenChange={switcher.setOpen} />}
          groups={groups}
          actions={actions}
          settingsSearch={venueSettingsTargets(scope, t)}
          organizationId={venue.organizationId}
          status={<ShiftStatus venue={venue} />}
          sidebarFoot={<OrganizationLink scope={scope} />}
          staffMenuItems={staffItems}
        >
          {/* Keyed by the venue: switching venues keeps the page (the switcher), never one venue's half-done state. */}
          <Outlet key={venue.id} />
        </AppShell>
        </ShiftPromptProvider>
      </RealtimeProvider>
      {tour.element}
    </ScopeProvider>
  );
}

/** Does the member hold this built-in role (better-auth's comma list)? */
function isRole(held: string | undefined, role: string): boolean {
  return held !== undefined && held.split(',').some((part) => part.trim() === role);
}

/**
 * The sidebar with an owner's "Setup · n left" first in its Setup group (spec/13 §13.1): there while a venue sets up,
 * counting what is left, gone once every venue has opened. Without an entry, the groups as they were.
 */
function withSetupEntry(groups: NavGroup[], entry: SetupNavEntry | undefined, to: SectionRoute | undefined, t: ConsoleT): NavGroup[] {
  if (entry === undefined || to === undefined) return groups;
  const item = {
    id: 'setup',
    label: t('setup.nav'),
    icon: <NavIcon name="setup" />,
    to: to.to,
    params: to.params,
    ...(entry.left > 0 ? { badge: { text: t('setup.navLeft', { count: entry.left }), label: t('setup.navLabel', { count: entry.left }) } } : {}),
  };
  const home = groups.find((group) => group.id === 'setup');
  if (home === undefined) return [...groups, { id: 'setup', label: t('shell.group.setup'), items: [item] }];
  return groups.map((group) => (group.id === 'setup' ? { ...group, items: [item, ...group.items] } : group));
}

type ConsoleT = ReturnType<typeof useTranslation>['t'];
interface SectionRoute {
  readonly to: string;
  readonly params: Record<string, string>;
}

/** Turn the grouped section specs into the shell's nav, with each section's route. */
function buildGroups(specs: readonly GroupSpec[], t: ConsoleT, resolve: (section: SectionSpec) => SectionRoute): NavGroup[] {
  return specs.map((group) => ({
    id: group.id,
    label: t(`shell.group.${group.id}`),
    items: group.sections.map((section) => {
      const { to, params } = resolve(section);
      return {
        id: section.id,
        label: t(`shell.nav.${section.id}`),
        icon: <NavIcon name={section.id} />,
        to,
        params,
        // The venue's Floor is its root path: current there alone, not on every page below it.
        ...(section.id === 'floor' ? { exact: true } : {}),
      };
    }),
  }));
}

export function OrganizationLayout(): ReactNode {
  const { t } = useTranslation('console');
  const { orgSlug } = useParams({ from: '/signed-in/o/$orgSlug' });
  const organizations = useMyOrganizations();
  // No first-visit tour at the organisation: its sidebar has few of the tour's stops. The staff menu still plays it.
  const tour = useConsoleTour(false);
  const staffItems = useStaffMenuItems({ to: '/o/$orgSlug/account', params: { orgSlug } }, tour.start);
  const venues = useMyVenues();
  const found = organizations.data?.find((candidate) => candidate.slug === orgSlug);
  const switcher = useVenueSwitcher(found === undefined ? undefined : { kind: 'organization', id: found.id });
  const actions = useAreaActions(switcher.action);
  const setupEntry = useSetupNavEntry(found, venues.data);
  // What the member may open at the organisation decides what the sidebar lists (spec/13 §1, §2).
  const access = useAccess(found === undefined ? undefined : { organizationId: found.id });

  if (organizations.data === undefined) {
    return organizations.isError ? (
      <LoadFailure error={organizations.error} onRetry={() => void organizations.refetch()} />
    ) : (
      <Loading />
    );
  }
  const organization = found;
  if (organization === undefined) return <NotFound />;

  const scope: ConsoleScope = { kind: 'organization', organization };
  const sectionGroups = buildGroups(visibleGroups(ORG_GROUPS, access), t, (section): SectionRoute => {
    if (section.id === 'settings') return { to: '/o/$orgSlug/settings', params: { orgSlug } };
    if (section.id === 'players') return { to: '/o/$orgSlug/players', params: { orgSlug } };
    // Venues (F-ORG-01, spec/13 §13): the organisation's venues, and its franchisees' (F-ORG-17).
    if (section.id === 'venues') return { to: '/o/$orgSlug/venues', params: { orgSlug } };
    if (section.id === 'staffAndRoles') return { to: '/o/$orgSlug/staff', params: { orgSlug } };
    if (section.id === 'audit') return { to: '/o/$orgSlug/audit', params: { orgSlug } };
    if (section.id === 'addons') return { to: '/o/$orgSlug/add-ons', params: { orgSlug } };
    if (section.id === 'pricing') return { to: '/o/$orgSlug/pricing', params: { orgSlug } };
    return { to: '/o/$orgSlug/soon/$section', params: { orgSlug, section: section.id } };
  });
  const groups = withSetupEntry(sectionGroups, setupEntry, { to: '/o/$orgSlug/setup', params: { orgSlug } }, t);
  return (
    <ScopeProvider scope={scope}>
      <AppShell
        area={<VenueSwitcher scope={scope} open={switcher.open} onOpenChange={switcher.setOpen} />}
        groups={groups}
        actions={actions}
        settingsSearch={organizationSettingsTargets(organization, venues.data ?? [], t)}
        organizationId={organization.id}
        // In the organisation's own area its settings are the sidebar's Settings: no second link to them at its foot.
        staffMenuItems={staffItems}
      >
        <Outlet key={organization.id} />
      </AppShell>
      {tour.element}
    </ScopeProvider>
  );
}

/** The most venues an organisation's palette searches the settings of, beside the organisation's own. */
const MAX_SEARCHED_VENUES = 8;

function venueTarget(venue: MyVenue, t: ConsoleT): SettingsTarget {
  return {
    id: `venue:${venue.id}`,
    scope: { kind: 'venue', organizationId: venue.organizationId, venueId: venue.id },
    scopeLabel: t('palette.settings.scope.venue', { venue: venue.name }),
    to: '/v/$venueSlug/settings/$group',
    params: { venueSlug: venue.slug },
    venueProfile: true,
  };
}

function organizationTarget(organization: MyOrganization, t: ConsoleT): SettingsTarget {
  return {
    id: `organization:${organization.id}`,
    scope: { kind: 'organization', organizationId: organization.id },
    scopeLabel: t('palette.settings.scope.organization'),
    to: '/o/$orgSlug/settings/$group',
    params: { orgSlug: organization.slug },
  };
}

/**
 * At a venue: its own settings, and the organisation's when the member's staff access covers every
 * venue — a manager of one venue opens the organisation's screen read-only, so its settings are not
 * offered; what they see of them is on the venue's screen, read-only, where the server lists it.
 */
function venueSettingsTargets(scope: Extract<ConsoleScope, { kind: 'venue' }>, t: ConsoleT): SettingsTarget[] {
  const targets = [venueTarget(scope.venue, t)];
  if (scope.organization !== undefined && scope.organization.allVenues !== false) targets.push(organizationTarget(scope.organization, t));
  return targets;
}

/** At the organisation: its settings, then its venues' (the member's own), by name. */
function organizationSettingsTargets(organization: MyOrganization, venues: readonly MyVenue[], t: ConsoleT): SettingsTarget[] {
  const own = venues.filter((venue) => venue.organizationId === organization.id).slice(0, MAX_SEARCHED_VENUES);
  return [organizationTarget(organization, t), ...own.map((venue) => venueTarget(venue, t))];
}

/**
 * The palette's actions, at a venue and at the organisation alike: "Switch venue or organisation" — the top bar's
 * switcher, opened — where there is somewhere else to go, and sign out.
 */
function useAreaActions(switchPlace: PaletteAction | undefined): PaletteAction[] {
  const { t } = useTranslation('console');
  const signOut = useSignOut();
  return [
    ...(switchPlace === undefined ? [] : [switchPlace]),
    { id: 'sign-out', label: t('shell.signOut'), icon: <NavIcon name="signout" />, run: () => void signOut() },
  ];
}

/**
 * Has the member no staff PIN at this venue yet (F-IAM-10)? Read from the server's own list of where
 * they may set one (`GET …/members/me/pins`), so it is never asked of a venue they cannot set one at.
 * `false` until the list is read, when it cannot be, and for a PIN that is locked — that one needs a
 * manager, not a nudge.
 */
function useStaffPinMissing(venue: MyVenue | undefined): boolean {
  const pins = useMyStaffPins(venue?.organizationId ?? '', venue !== undefined);
  const here = venue === undefined ? undefined : pins.data?.find((one) => one.venueId === venue.id);
  return here !== undefined && !here.set && !here.locked;
}

/**
 * The staff menu's entries: your account in this area. At a venue where
 * the member has no staff PIN yet, "Your account" carries one quiet line saying so — the page it opens
 * is where it is set.
 */
function useStaffMenuItems(account: SectionRoute, startTour: () => void, pinMissing = false) {
  const { t } = useTranslation('console');
  const navigate = useNavigate();
  const label = pinMissing ? (
    <span className="console-user-menu__entry">
      {t('shell.account')}
      {/* Read as "Your account, Set your staff PIN for this venue" rather than as one run-on word. */}
      <span className="clav-visually-hidden">, </span>
      <span className="console-user-menu__prompt">{t('shell.accountPinPrompt')}</span>
    </span>
  ) : (
    t('shell.account')
  );
  return [
    // Choosing another venue is the top bar's venue switcher (and the palette's action); the menu does not offer it again.
    { id: 'account', label, icon: <NavIcon name="staff" />, onSelect: () => void navigate(account as never) },
    // The console tour again (spec/13 §2): its stops are the sidebar's, so it plays wherever the sidebar is drawn.
    { id: 'tour', label: t('tour.take'), icon: <Icon name="info" />, onSelect: startTour },
  ];
}

/** A page still loading: a quiet skeleton of a header and a body in the page's place. */
export function Loading(): ReactNode {
  const { t } = useTranslation('console');
  return (
    <div className="console-loading" role="status" aria-busy="true" aria-label={t('app.loading')}>
      <Skeleton shape="text" width="12rem" height="1.75rem" />
      <Skeleton shape="text" width="20rem" />
      <Skeleton height="10rem" />
    </div>
  );
}
